import clsx from 'clsx';
import { useState } from 'react';
import { useStore } from '../store';
import { ActionLabelModal } from './ActionLabelModal';

interface Segment {
  // start and end are in SECONDS (float for fps>1), not bit indices.
  start: number;
  end: number;
}

function deriveSegments(
  labels: Record<string, boolean | null>,
  totalBits: number,
  fps: number = 1
): Segment[] {
  if (totalBits <= 0) return [];

  const effectiveFps = Math.max(1, Math.floor(fps));

  // Boundaries are bit indices where labels[i] === false (New Action marker).
  // Segment start/end are converted to seconds so downstream APIs (clip,
  // save, LLM) receive the correct float timestamps regardless of fps.
  const boundaries: number[] = [];
  for (let i = 1; i < totalBits; i++) {
    if (labels[String(i)] === false) {
      boundaries.push(i);
    }
  }

  const segments: Segment[] = [];
  let startBit = 0;
  for (const boundaryBit of boundaries) {
    segments.push({ start: startBit / effectiveFps, end: boundaryBit / effectiveFps });
    startBit = boundaryBit;
  }
  segments.push({ start: startBit / effectiveFps, end: totalBits / effectiveFps });
  return segments;
}

type SegmentStatus = 'saved' | 'ready' | 'clipping' | 'loading' | 'error' | 'unlabeled';

export function ActionLabelsSidebar() {
  const {
    labels,
    images,
    previewVideoUrl,
    selectedEpisode,
    operator,
    statsCache,
    myStatsCache,
    actionLabels,
    actionLabelsLlmState,
    currentEpisodeAnnotationFps,
    triggerLlmForSegmentEnding,
    saveActionLabelToStore,
    deleteActionLabelFromStore,
  } = useStore();

  const [selectedStartSecond, setSelectedStartSecond] = useState<number | null>(null);

  const fps = Math.max(1, Math.floor(currentEpisodeAnnotationFps || 1));
  const totalBits = images.length; // frame count = duration * fps (storage bit count)
  const segments = deriveSegments(labels, totalBits, fps);

  const features = myStatsCache?.features ?? statsCache?.features;
  if (!features?.llm_action_labels || !selectedEpisode) {
    return null;
  }

  function getSegmentStatus(seg: Segment): SegmentStatus {
    if (actionLabels[seg.start]) return 'saved';
    const llmState = actionLabelsLlmState[seg.start];
    if (!llmState) return 'unlabeled';
    if (llmState.status === 'clipping') return 'clipping';
    if (llmState.status === 'loading') return 'loading';
    if (llmState.status === 'ready') return 'ready';
    if (llmState.status === 'error') return 'error';
    return 'unlabeled';
  }

  function getDotColor(status: SegmentStatus): string {
    switch (status) {
      case 'saved':
        return 'bg-emerald-500';
      case 'ready':
        return 'bg-yellow-400';
      case 'clipping':
      case 'loading':
        return 'bg-orange-400 animate-pulse';
      case 'error':
        return 'bg-red-500';
      default:
        return 'bg-brand-border';
    }
  }

  const selectedSeg =
    selectedStartSecond !== null
      ? (segments.find((s) => s.start === selectedStartSecond) ?? null)
      : null;

  const selectedLlmState =
    selectedStartSecond !== null ? actionLabelsLlmState[selectedStartSecond] : null;

  function handleDescribe(seg: Segment) {
    const llmStatus = actionLabelsLlmState[seg.start]?.status;
    if (llmStatus !== 'clipping' && llmStatus !== 'loading' && llmStatus !== 'ready') {
      void triggerLlmForSegmentEnding(seg.end);
    }
  }

  return (
    <>
      <div
        className="flex flex-col border-l border-brand-border bg-brand-surface"
        style={{ width: 280, flexShrink: 0 }}
      >
        {/* Header */}
        <div className="border-b border-brand-border px-3 py-2.5">
          <p className="text-xs font-semibold text-brand-text-primary">Action Labels</p>
          <p className="text-[10px] text-brand-text-secondary mt-0.5">
            {segments.length} segment{segments.length !== 1 ? 's' : ''}
          </p>
        </div>

        {/* Nav dots */}
        <div className="flex flex-wrap gap-1.5 border-b border-brand-border px-3 py-2">
          {segments.map((seg) => {
            const status = getSegmentStatus(seg);
            return (
              <button
                key={seg.start}
                type="button"
                onClick={() => setSelectedStartSecond(seg.start)}
                title={`${seg.start}s–${seg.end}s: ${status}`}
                className={clsx(
                  'h-2.5 w-2.5 rounded-full transition-transform hover:scale-125',
                  getDotColor(status),
                  selectedStartSecond === seg.start &&
                    'ring-2 ring-white ring-offset-1 ring-offset-brand-surface'
                )}
              />
            );
          })}
        </div>

        {/* Segment list */}
        <div className="flex-1 overflow-y-auto">
          {segments.length === 0 && (
            <p className="px-3 py-4 text-xs text-brand-text-secondary">
              No segments yet. Mark boundaries in the viewer.
            </p>
          )}
          {segments.map((seg) => {
            const status = getSegmentStatus(seg);
            const saved = actionLabels[seg.start];
            const llmState = actionLabelsLlmState[seg.start];
            const isActive = selectedStartSecond === seg.start;
            const isBusy = status === 'clipping' || status === 'loading';

            return (
              <div
                key={seg.start}
                className={clsx(
                  'border-b border-brand-border px-3 py-2.5 transition-colors',
                  isActive ? 'bg-brand-surface-highlight' : 'hover:bg-brand-surface-highlight/50'
                )}
              >
                {/* Time range row */}
                <button
                  type="button"
                  onClick={() => setSelectedStartSecond(seg.start)}
                  className="flex w-full items-center justify-between text-left"
                >
                  <span className="text-xs font-mono text-brand-text-secondary">
                    {seg.start}s &ndash; {seg.end}s
                  </span>
                  <span
                    className={clsx(
                      'text-[10px] font-medium',
                      status === 'saved' && 'text-emerald-400',
                      (status === 'clipping' || status === 'loading') && 'text-orange-400',
                      status === 'error' && 'text-red-400',
                      status === 'unlabeled' && 'text-brand-text-secondary'
                    )}
                  >
                    {status === 'saved' && '✓ Saved'}
                    {status === 'ready' && '● Pick a candidate'}
                    {status === 'clipping' && 'Clipping...'}
                    {status === 'loading' && 'Analyzing...'}
                    {status === 'error' && 'Error'}
                    {status === 'unlabeled' && 'Unlabeled'}
                  </span>
                </button>

                {/* Saved text preview */}
                {saved && (
                  <p className="mt-1 text-[11px] text-brand-text-primary line-clamp-2">
                    {saved.final_text_zh ?? saved.final_text}
                  </p>
                )}

                {/* Error message */}
                {status === 'error' && llmState?.error && (
                  <p className="mt-1 text-[10px] text-red-400 line-clamp-1">{llmState.error}</p>
                )}

                {/* Action buttons */}
                <div className="mt-1.5 flex items-center gap-1.5">
                  {isBusy ? (
                    <span className="text-[10px] text-orange-400">Working...</span>
                  ) : saved ? (
                    <button
                      type="button"
                      onClick={() => setSelectedStartSecond(seg.start)}
                      className="rounded px-2 py-0.5 text-[10px] font-medium border border-brand-border text-brand-text-secondary hover:text-white hover:border-brand-orange/50 transition-colors"
                    >
                      Edit
                    </button>
                  ) : llmState?.status === 'ready' ? (
                    <button
                      type="button"
                      onClick={() => setSelectedStartSecond(seg.start)}
                      className="rounded px-2 py-0.5 text-[10px] font-medium border border-yellow-500/50 text-yellow-400 hover:text-white hover:border-brand-orange/50 transition-colors"
                    >
                      Pick
                    </button>
                  ) : (
                    <button
                      type="button"
                      disabled={!previewVideoUrl}
                      onClick={() => void handleDescribe(seg)}
                      className="rounded px-2 py-0.5 text-[10px] font-medium border border-brand-border text-brand-text-secondary hover:text-white hover:border-brand-orange/50 transition-colors disabled:opacity-40"
                    >
                      Describe
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Modal */}
      {selectedStartSecond !== null && selectedSeg !== null && (
        <ActionLabelModal
          startSecond={selectedSeg.start}
          endSecond={selectedSeg.end}
          episodeId={selectedEpisode}
          operator={operator}
          llmStatus={selectedLlmState?.status ?? 'idle'}
          candidates={selectedLlmState?.candidates ?? []}
          llmError={selectedLlmState?.error ?? null}
          model={selectedLlmState?.model ?? null}
          llmDurationS={selectedLlmState?.llmDurationS ?? null}
          existingLabel={actionLabels[selectedStartSecond] ?? null}
          onSave={async (start, payload) => {
            await saveActionLabelToStore(start, payload);
          }}
          onDelete={async (start) => {
            await deleteActionLabelFromStore(start);
          }}
          onClose={() => setSelectedStartSecond(null)}
        />
      )}
    </>
  );
}
